import React, { FC } from 'react';
import { connect } from 'react-redux';
import classNames from 'classnames';
import { compose } from '@reduxjs/toolkit';

import type { BettingContextProps } from '../../../../../../types';
import type { ScoreboardCellsProps } from '../../types';
import type { ReduxState } from 'app/redux/types';

import { withinBettingContext } from 'app/contexts/betting';
import { useTranslation } from 'app/lang';
import {
  HandballPeriodType,
  HandballScoreType, type ScoresEntityByTeamId,
  type ScoresPartOfGameEntity,
} from 'app/lib/betting/betting-entities';
import { TeamId, TEAMS } from 'app/lib/betting/constants';

import MainScoreWrapper from '../MainScoreWrapper';
import { ScoreboardCell, ScoreboardCellWrapper } from '../ScoreboardCell';

import dictionary from './Handball.dictionary.json';

import styles from '../../ScoreboardCells.module.scss';

type StoreProps = {
  firstTimeScore: ScoresEntityByTeamId;
  secondTimeScore: ScoresEntityByTeamId;
  overtimeScores: ScoresPartOfGameEntity;
  penaltyScores: ScoresEntityByTeamId;
  isPeriodFinished: boolean;
  isOvertimeFinished: boolean;
  isPenaltyFinished: boolean;
  isTimeBreak: boolean;
  matchPeriodNumber: number;
};

const {
  GOAL,
} = HandballScoreType;

const {
  TIME,
  PENALTY,
  COMPENSATED_TIME,
  BREAK,
} = HandballPeriodType;

const HandballScoreboardCellsWithoutHOC: FC<ScoreboardCellsProps & StoreProps> = ({
  firstTimeScore,
  secondTimeScore,
  overtimeScores,
  penaltyScores,
  isPeriodFinished,
  isOvertimeFinished,
  isPenaltyFinished,
  isTimeBreak,
  matchPeriodNumber,
  matchId,
}) => {
  const { t } = useTranslation();
  const cells = [];

  if (firstTimeScore !== null) {
    const className = classNames({ [styles.firstPeriodScore]: !secondTimeScore });
    const content = isTimeBreak && !secondTimeScore
      ? t(dictionary.mainPayment)
      : t(dictionary.periodTitle, { periodNumber: 1 });

    cells.push(
      <ScoreboardCellWrapper content={content}
                             key="fps">
        {TEAMS.map((teamId) => (
          <ScoreboardCell className={className}
                          key={teamId}>
            {firstTimeScore.get(teamId, '–')}
          </ScoreboardCell>
        ))}
      </ScoreboardCellWrapper>,
    );
  }

  if (secondTimeScore !== null) {
    const cellWrapperClassName = classNames({
      [styles.currentPeriodScore]: (
        matchPeriodNumber === TeamId.SECOND
        && !isPeriodFinished
      ),
    });

    cells.push(
      <ScoreboardCellWrapper content={t(dictionary.periodTitle, { periodNumber: 2 })}
                             className={cellWrapperClassName}
                             key="sps">
        {TEAMS.map((teamId) => (
          <ScoreboardCell key={teamId}>
            {secondTimeScore.get(teamId, '–')}
          </ScoreboardCell>
        ))}
      </ScoreboardCellWrapper>,
    );
  }

  if (overtimeScores !== null) {
    const currentOvertimeScores = overtimeScores.last();
    const overtimeComponent = (scores: ScoresEntityByTeamId, periodNumber: number) => {
      const isStartOvertime = periodNumber <= TeamId.FIRST;
      const cellWrapperClassName = classNames({
        [styles.currentPeriodScore]: (
          scores === currentOvertimeScores
          && !penaltyScores && !isOvertimeFinished
        ),
        [styles.scoreDivider]: isStartOvertime,
      });
      return (
        <ScoreboardCellWrapper content={t(dictionary.overtimeTitle, { periodNumber })}
                               className={cellWrapperClassName}
                               isDivWrapperTag={isStartOvertime}
                               key={`${periodNumber}overtime-scores`}>
          {TEAMS.map((teamId) => (
            <ScoreboardCell key={teamId}>
              {scores.get(teamId, '–')}
            </ScoreboardCell>
          ))}
        </ScoreboardCellWrapper>
      );
    };

    overtimeScores.forEach((scores, periodNumber) => {
      if (periodNumber <= TeamId.FIRST) {
        cells.push(
          <li className={styles.mainScoreWrapper}
              key={`${periodNumber}overtime-scores`}>
            {overtimeComponent(scores, periodNumber)}
          </li>,
        );
      }
      else {
        cells.push(overtimeComponent(scores, periodNumber));
      }
    });
  }

  if (penaltyScores !== null) {
    const cellWrapperClassName = classNames({
      [styles.currentPeriodScore]: !isPenaltyFinished,
      [styles.scoreDivider]: penaltyScores,
    });

    cells.push(
      <li className={styles.mainScoreWrapper}
          key="pps">
        <ScoreboardCellWrapper content={t(dictionary.penalty)}
                               className={cellWrapperClassName}
                               isDivWrapperTag
                               key={`${matchId}-pps`}>
          {TEAMS.map((teamId) => (
            <ScoreboardCell key={teamId}>
              {penaltyScores.get(teamId, '–')}
            </ScoreboardCell>
          ))}
        </ScoreboardCellWrapper>
      </li>,
    );
  }

  if (secondTimeScore !== null) {
    cells.push(
      <MainScoreWrapper matchId={matchId}
                        key="main-score" />,
    );
  }

  return <>{cells}</>;
};

const mapStateToProps = (state: ReduxState, {
  $betting: {
    content: {
      selectors: {
        matches,
      },
    },
  },
  matchId,
}: BettingContextProps): StoreProps => {
  const match = matches(state).get(matchId);
  const scores = match.scores;
  const periods = match.periods;

  return {
    firstTimeScore: scores.getIn([GOAL, TIME, TeamId.FIRST], null),
    secondTimeScore: scores.getIn([GOAL, TIME, TeamId.SECOND], null),
    overtimeScores: scores.getIn([GOAL, COMPENSATED_TIME], null),
    penaltyScores: scores.getIn([GOAL, PENALTY, 0], null),
    isPeriodFinished: periods.get(TIME)?.finished,
    isOvertimeFinished: periods?.get(COMPENSATED_TIME)?.finished,
    isPenaltyFinished: periods?.get(PENALTY)?.finished,
    isTimeBreak: match.getIn(['periods', BREAK], null),
    matchPeriodNumber: periods.get(TIME)?.number,
  };
};

export const HandballScoreboardCells = compose<FC<ScoreboardCellsProps>>(
  withinBettingContext,
  connect(mapStateToProps),
)(HandballScoreboardCellsWithoutHOC);
